import { useState } from 'react';
import axiosInstance from 'interceptor/axiosInstance';
import { ApiRoutes } from 'routes/routeConstants/apiRoutes';
import { useNavigate, generatePath } from 'react-router-dom';
import { AppRoutes } from 'routes/routeConstants/appRoutes';
import { deserialize, serialize } from 'serializr';
import { RawMaterialIngredientsMeta } from 'models/RawMaterialIngredientsMeta/rawMaterialIngredientsMeta.model';
import { RawMaterialPackagingMeta } from 'models/RawMaterialPackagingMeta/rawMaterialPackagingMeta.model';
import { MultipleOriginsProductRawMaterial } from 'models/MultipleOriginsProductRawMaterial/multipleOriginsProductRawMaterial.model';
import { ProductTransportation } from 'models/ProductTransportation/productTransportation.model';
import { ProductDistributionData } from 'models/ProductDistributionData/ProductDistributionData.model';
import { MultipleOriginsProductRawMaterialTransportations } from 'models/MultipleOriginsProductRawMaterialTransportations/multipleOriginsProductRawMaterialTransportations.model';
import { BestFitRegion } from 'models/BestFitRegion/bestFitRegion.model';

const Stage1AndStage2ProductService = () => {
  const navigate = useNavigate();
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [rawMaterialIngredientsMeta, setRawMaterialIngredientsMeta] = useState<RawMaterialIngredientsMeta[]>([]);
  const [rawMateialPackagingMeta, setrawMateialPackagingMeta] = useState<RawMaterialPackagingMeta[]>([]);
  const [rawMaterialList, setRawMaterialList] = useState<MultipleOriginsProductRawMaterial[]>([]);
  const [productDistributionList, setProductDistributionList] = useState<ProductDistributionData>();
  const [transportationList, setTransportationList] = useState<MultipleOriginsProductRawMaterialTransportations[]>([]);
  const [bestFitRegion, setBestFitRegion] = useState<BestFitRegion[]>([]);

  /** Create Product - Stage 1(Raw Material) */
  const createProductRawMaterial = async (
    id: number,
    rawMaterial: MultipleOriginsProductRawMaterial
  ) => {
    try {
      setButtonLoading(true);
      const url = generatePath(ApiRoutes.CREATE_PRODUCT_STAGE_1, { id });
      const desiredPayload = serialize(
        MultipleOriginsProductRawMaterial,
        rawMaterial
      );
      const response = await axiosInstance.post(url, desiredPayload);
      const message = response.data?.message;
      navigate(
        generatePath(
          `${AppRoutes.CREATE_PRODUCT}/${AppRoutes.CREATE_PRODUCT_RAW_MATERIAL_TRANSPORTATION}`,
          { id: id }
        )
      );
      return message;
    } catch (error) {
    } finally {
      setButtonLoading(false);
    }
  };

  /** (Meta data): Fetch ingredients name - Stage1 (raw material) */
  const fetchIngredients = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        ApiRoutes.META_INGREDIENTS_STAGE_1
      );
      const data = deserialize(
        RawMaterialIngredientsMeta,
        response.data
      ) as RawMaterialIngredientsMeta[];
      setRawMaterialIngredientsMeta(data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  /** (Meta data): Fetch ingredient origin */
  const fetchIngredientOrigin = async (params?: {
    activity_label?: string;
  }) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        ApiRoutes.META_INGREDIENTS_ORIGIN,
        { params }
      );
      const data = deserialize(BestFitRegion, response.data) as BestFitRegion[];
      setBestFitRegion(data);
      return data;
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const resetBestFitRegion = () => setBestFitRegion([]);

  /** (Meta data): Fetch Packaing material name - Stage1 (raw material) */
  const fetchPackagingMaterials = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        ApiRoutes.META_PACKAGING_STAGE_1
      );
      const data = deserialize(
        RawMaterialPackagingMeta,
        response.data
      ) as RawMaterialPackagingMeta[];
      setrawMateialPackagingMeta(data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  /** Create trasnportation(Create product flow) */
  const createTransportation = async (
    productId: number,
    payload: ProductTransportation[]
  ) => {
    try {
      setButtonLoading(true);
      const url = generatePath(ApiRoutes.CREATE_PRODUCT_STAGE_2, {
        id: productId,
      });
      await axiosInstance.post(url, payload);
      navigate(
        generatePath(
          `${AppRoutes.CREATE_PRODUCT}/${AppRoutes.CREATE_PRODUCT_PROCESSING_FACILITY}`,
          { id: productId }
        )
      );
    } catch (error) {
    } finally {
      setButtonLoading(false);
    }
  };

  /** (Meta data): Fetch Raw Material Data - Stage1 (raw material) */
  const fetchRawMaterialListing = async (id: number) => {
    try {
      setLoading(true);
      const url = generatePath(ApiRoutes.FETCH_STAGE_1_LIST, { id });
      const response = await axiosInstance.get(url);
      const data = deserialize(
        MultipleOriginsProductRawMaterial,
        response.data['data']['productRawMaterials']
      ) as MultipleOriginsProductRawMaterial[];
      setRawMaterialList(data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  /** (Meta data): Fetch Raw Material Data - Stage1 (raw material) */
  const fetchProductDistributionListing = async (id: number) => {
    try {
      setLoading(true);
      const url = generatePath(ApiRoutes.FETCH_STAGE_4_LIST, { id });
      const response = await axiosInstance.get(url);
      const data = deserialize(
        ProductDistributionData,
        response.data['data']
      ) as ProductDistributionData;
      setProductDistributionList(data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  /** (Meta data): Fetch Transportation Listing - Stage2 (Transportation) */
  const fetchTranportationListing = async (id: number) => {
    try {
      setLoading(true);
      const url = generatePath(ApiRoutes.FETCH_STAGE_2_LIST, { id });
      const response = await axiosInstance.get(url);
      const data = deserialize(
        MultipleOriginsProductRawMaterialTransportations,
        response.data.data['productRawMaterialTransportations']
      ) as MultipleOriginsProductRawMaterialTransportations[];
      setTransportationList(data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  /** Edit Create Product Flow: Stage1 */
  const updateRawMaterial = async (
    id: number,
    rawMaterial: MultipleOriginsProductRawMaterial
  ) => {
    try {
      setButtonLoading(true);
      const url = generatePath(ApiRoutes.EDIT_STAGE1, { id });
      const desiredPayload = serialize(
        MultipleOriginsProductRawMaterial,
        rawMaterial
      );
      const response = await axiosInstance.put(url, desiredPayload);
      const message = response.data?.message;
      navigate(
        generatePath(
          `${AppRoutes.CREATE_PRODUCT}/${AppRoutes.CREATE_PRODUCT_RAW_MATERIAL_TRANSPORTATION}`,
          { id: id }
        )
      );
      return message;
    } catch (error) {
    } finally {
      setButtonLoading(false);
    }
  };

  return {
    buttonLoading,
    loading,
    rawMaterialIngredientsMeta,
    rawMateialPackagingMeta,
    rawMaterialList,
    bestFitRegion,
    transportationList,
    productDistributionList,
    createProductRawMaterial,
    fetchIngredients,
    fetchPackagingMaterials,
    fetchRawMaterialListing,
    fetchIngredientOrigin,
    createTransportation,
    fetchProductDistributionListing,
    fetchTranportationListing,
    updateRawMaterial,
    resetBestFitRegion,
  };
};

export default Stage1AndStage2ProductService; 